import { Injectable } from '@angular/core';
import { map, Observable,BehaviorSubject } from 'rxjs';
import { IndexedDBService } from './indexed.db.service';
import { QuoteSummaryDTO, RfqPartDTO, PartQuantityDTO } from '../app/quote-summary/quote.summary.dto';
import { ApiService } from './api.service';
import { BaseService } from './base.service';
import { QuoteAssistModel } from '../app/quote-assist/quote-assist.model';
import { Rfq, RfqPart } from '../app/quote-assist/rfq.model';

@Injectable({
    providedIn: 'root'
})
export class QuoteSummaryService extends BaseService {
    private continueQuotingSubject =new BehaviorSubject<any>(null);
    continueQuoting$ = this.continueQuotingSubject.asObservable();

    triggerContinueQuote(data: any) {
        console.log('triggerContinueQuote', data);
        this.continueQuotingSubject.next(data);
    }
    constructor(
        private apiService: ApiService,
        private indexedDBService: IndexedDBService) {
        super();
    }

    /**
     * Loads quote summary data based on the provided `quoteAssistDataId`.
     * @param quoteAssistDataId - The ID of the quote assist data to load.
     * @returns An Observable containing the processed quote summary data.
     */
    loadQuoteSummaryData(data: any): Observable<any | null> {
        return this.indexedDBService.getDataById(data.rfqId).pipe(
            map((indexedData: Rfq) => {
                // console.log("rfqDetail", indexedData)
                if (!indexedData) {
                    return null;
                }
                let rfqDetail: QuoteSummaryDTO = this.initializeQuoteSummaryDTO();
                rfqDetail = Object.assign(rfqDetail, indexedData);
                const rfqPartList = this.processRfqPartList(rfqDetail.rfqPartList, data.rfqPartId);
                rfqDetail.doesAllPartCompleted = this.doesAllPartCompleted,
                    rfqDetail.quoteNumber = "";
                rfqDetail.rfqPartList = rfqPartList;
                return {"rfqDetail": rfqDetail, "assistData": indexedData}
            })
        );
    }

    initializeQuoteSummaryDTO(): QuoteSummaryDTO {
        return {
          rfqId: 0, // Default value for number
          rfqName: '', // Default value for string
          quoteExpiryDate: '', // Default value for string (you can use a date format like 'YYYY-MM-DD')
          companyId: 0, // Default value for number
          supplierId: '', // Default value for string
          rfqPartList: [], // Initialize as an empty array of RfqPartDTO
          buyerAddress: '', // Default value for string
          buyerEmail: '', // Default value for string
          isAllPartCompleted: false, // Default value for boolean
          doesAllPartCompleted: (rfqPartList: RfqPartDTO[]) => {
            return rfqPartList.every(part => part.isPartCompleted);
          }, // Function to check if all parts are completed
          supplierMetaData: null, // Default value for nullable object
          data: null, // Default value for nullable object
          quoteNumber: '', // Default value for string
          paymentTerm: '', // Default value for string
        };
    }

    updateExistingData(quoteSummaryData: any): Observable<any> {
        return this.indexedDBService.addData(quoteSummaryData).pipe(
            map((rfqDetail) => {
                return rfqDetail
            })
        );
    }
    /**
     * Processes the RFQ part list to construct a structured list of parts with their quantities.
     * @param rfqPartList - The raw RFQ part list to process.
     * @param partId - selected partId.
     * @returns An array of `PartSection` objects.
     */
    private processRfqPartList(rfqPartList: RfqPartDTO[], partId: number): any {

        return rfqPartList.map((part) => {
            part.partQuantity.forEach((quantity: PartQuantityDTO, index: number) => {
                quantity.isVisible = index === 0
                quantity.quantityUnit = "Unit";
            })
            part.doesPartCompleted = this.doesPartCompleted;
            part.isVisible = partId === part.rfqPartId;
            return part;
        }).filter(Boolean); // Filter out any null values (e.g., parts that didn't meet the condition)
    }

    /**
     * Determines if a part is completed based on its quantity details.
     * @param partQuantity - The list of quantity details for a part.
     * @returns `true` if all quantities are completed, otherwise `false`.
     */
    doesPartCompleted = (partQuantity: PartQuantityDTO[]): boolean => {
        if (!partQuantity || partQuantity.length == 0) {
            return false;
        }
        const completedCount = partQuantity.filter(q => q.qtyCompleted).length;
        return completedCount === partQuantity.length;
    }
    doesAllPartCompleted = (rfqPartList: RfqPartDTO[]): boolean => {
        const completedCount = rfqPartList.filter((q: any) => q.doesPartCompleted(q.partQuantity)).length;
        return completedCount > 0 && rfqPartList.length - completedCount == 0;
    }
    saveDraft(quoteAssistModel: any): Observable<number> {
        const draftData = JSON.parse(JSON.stringify(quoteAssistModel));
        return new Observable<number>(observer => {
            try {
                this.apiService.post<QuoteAssistModel>(
                    this.dynamoBaseApiUrl,
                    'supplier/SaveQASupplierRfqQuoteData',
                    draftData
                ).subscribe({
                    next: (response) => {
                        observer.next(response as unknown as number);
                        observer.complete();
                    },
                    error: (err) => {
                        observer.error(err);
                    }
                });
            } catch (error) {
                observer.error(error);
            }
        });
    }
}